<template>
  <div class="w-full max-w-7xl px-4 md:px-8 py-8 mx-auto">
    <h1 class="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Dashboard</h1>
    <p class="mb-8 text-gray-700 dark:text-gray-300">Welcome to the Research Control Panel.</p>
    <!-- Data Widgets Row -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow p-6 flex flex-col items-start">
        <span class="text-gray-500 dark:text-gray-400 text-sm mb-1">Active Projects</span>
        <span class="text-3xl font-bold text-blue-600 dark:text-blue-400">12</span>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow p-6 flex flex-col items-start">
        <span class="text-gray-500 dark:text-gray-400 text-sm mb-1">Participants</span>
        <span class="text-3xl font-bold text-green-600 dark:text-green-400">197</span>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow p-6 flex flex-col items-start">
        <span class="text-gray-500 dark:text-gray-400 text-sm mb-1">Pending Reviews</span>
        <span class="text-3xl font-bold text-yellow-500 dark:text-yellow-300">5</span>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow p-6 flex flex-col items-start">
        <span class="text-gray-500 dark:text-gray-400 text-sm mb-1">Alerts</span>
        <span class="text-3xl font-bold text-red-500 dark:text-red-400">2</span>
      </div>
    </div>
    <!-- Alerts -->
    <div class="mb-8 space-y-3">
      <div class="flex items-center bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
        <svg class="w-5 h-5 mr-2 text-red-400 dark:text-red-300" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z"/>
        </svg>
        <span>2 projects require immediate attention.</span>
      </div>
      <div class="flex items-center bg-yellow-50 dark:bg-yellow-900/40 border border-yellow-200 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300 px-4 py-3 rounded-lg">
        <svg class="w-5 h-5 mr-2 text-yellow-400 dark:text-yellow-200" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z"/>
        </svg>
        <span>5 reviews are pending.</span>
      </div>
    </div>
    <!-- Charts -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 mb-8">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow p-6 flex flex-col">
        <h2 class="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Monthly Activity</h2>
        <LineChart :data="lineChartData" :options="chartOptions" class="h-64" />
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow p-6 flex flex-col">
        <h2 class="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Daily Activity</h2>
        <LineChart :data="lineChartData" :options="chartOptions" class="h-64" />
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow p-6 flex flex-col">
        <h2 class="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Project Progress</h2>
        <BarChart :data="barChartData" :options="chartOptions" class="h-64" />
      </div>
    </div>
    <!-- Recent Activity List -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
      <h2 class="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Recent Activity</h2>
      <ul class="divide-y divide-gray-100 dark:divide-gray-700">
        <li class="py-3 flex items-center justify-between">
          <span class="text-gray-700 dark:text-gray-300">Project "Genome Study" updated</span>
          <span class="text-xs text-gray-400 dark:text-gray-500">2 hours ago</span>
        </li>
        <li class="py-3 flex items-center justify-between">
          <span class="text-gray-700 dark:text-gray-300">New participant registered</span>
          <span class="text-xs text-gray-400 dark:text-gray-500">4 hours ago</span>
        </li>
        <li class="py-3 flex items-center justify-between">
          <span class="text-gray-700 dark:text-gray-300">Review submitted for "Cancer Research"</span>
          <span class="text-xs text-gray-400 dark:text-gray-500">Yesterday</span>
        </li>
        <li class="py-3 flex items-center justify-between">
          <span class="text-gray-700 dark:text-gray-300">Alert: Consent form missing</span>
          <span class="text-xs text-red-400 dark:text-red-300">Yesterday</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { Line, Bar, Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, Filler, LineElement, PointElement, LinearScale, CategoryScale, BarElement, ArcElement } from 'chart.js'

ChartJS.register(Filler, LineElement, PointElement, LinearScale, CategoryScale, BarElement, ArcElement)

// Chart components
const LineChart = Line
const BarChart = Bar
const DoughnutChart = Doughnut

// Use plain objects, not ref()
const lineChartData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
  datasets: [
    {
      label: 'Activity',
      data: [40, 55, 65, 59, 80, 81, 56],
      borderColor: '#6366f1',
      backgroundColor: 'rgba(99,102,241,0.1)',
      tension: 0.4,
      fill: true,
    },
  ],
}

const barChartData = {
  labels: ['Project A', 'Project B', 'Project C', 'Project D'],
  datasets: [
    {
      label: 'Progress (%)',
      data: [80, 55, 40, 95],
      backgroundColor: ['#6366f1', '#f59e42', '#10b981', '#f43f5e'],
      borderRadius: 8,
    },
  ],
}

const doughnutChartData = {
  labels: ['Active', 'Pending', 'Completed'],
  datasets: [
    {
      label: 'Participants',
      data: [120, 45, 30],
      backgroundColor: ['#10b981', '#f59e42', '#6366f1'],
      borderWidth: 2,
    },
  ],
}

const chartOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        color: '#6b7280',
        font: { size: 14 },
      },
    },
  },
  scales: {
    x: {
      ticks: { color: '#6b7280' },
      grid: { color: '#e5e7eb' },
    },
    y: {
      ticks: { color: '#6b7280' },
      grid: { color: '#e5e7eb' },
    },
  },
}
</script>

<style scoped>
/* Optional: make charts responsive */
.chartjs-render-monitor {
  width: 100% !important;
  height: 100% !important;
}
</style>